import React, { useState } from 'react';

import CloseIcon from '../../../../assets/icons/modalsIcons/CloseIcon';
import { FeedbackModalProps } from './types';
import FeedbackForm from './FeedbackForm';
import FeedbackSuccessMessage from './FeedbackSuccessMessage';
import { cropConnectApiClient } from '../../../../services/cropConnectApiClient';
import { useAppInsightsComponentLifetimeTracking } from '../../../../services/appInsights/useAppInsightsComponentTracking';
import {
  DialogContentWrapper,
  DialogTitleWrapper,
  DialogWrapper,
  ModalContentWrapper,
  ModalInstructions,
  ModalInstructionsThanks,
  ModalSubtitle,
  ModalTitle,
  StyledCloseIcon,
} from './styles';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onClose,
  onDiscard,
}) => {
  const { t } = useI18n();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useAppInsightsComponentLifetimeTracking('FeedbackModal');

  const handleSubmit = async (message: string): Promise<void> => {
    if (message) {
      try {
        const response = await cropConnectApiClient.addFeedback(false, {
          body: message,
        });
        if (response.success) {
          setIsFormSubmitted(true);
          setError(null);
        } else {
          setError(t(TranslationKeys.feedbackModal_error_generic) as string);
        }
      } catch (e) {
        setError(t(TranslationKeys.feedbackModal_error_submission) as string);
        console.error(e);
      }
    }
  };

  const handleReset = () => {
    setIsFormSubmitted(false);
    if (onClose) {
      onClose();
    }
  };

  const handleDiscard = () => {
    setIsFormSubmitted(false);
    if (onDiscard) {
      onDiscard();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setIsFormSubmitted(false);
  };

  return (
    <DialogWrapper open={open} onClick={handleClose}>
      <div onClick={(e) => e.stopPropagation()}>
        <DialogTitleWrapper>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '32px 24px 0 24px',
            }}
          >
            <ModalTitle>
              {isFormSubmitted
                ? t(TranslationKeys.feedbackModal_title_thankYou)
                : t(TranslationKeys.feedbackModal_title_yourFeedback)}
            </ModalTitle>
            <StyledCloseIcon onClick={handleClose}>
              <CloseIcon />
            </StyledCloseIcon>
          </div>
        </DialogTitleWrapper>
        <DialogContentWrapper>
          <ModalContentWrapper>
            {!isFormSubmitted ? (
              <>
                {error && (
                  <div style={{ color: 'red', margin: '15px 0' }}>{error}</div>
                )}
                <ModalSubtitle>
                  {t(TranslationKeys.feedbackModal_subtitle_help)}
                </ModalSubtitle>
                <ModalInstructions>
                  {t(TranslationKeys.feedbackModal_instructions)}
                </ModalInstructions>
                <ModalInstructionsThanks>
                  {t(TranslationKeys.feedbackModal_thanks)}
                </ModalInstructionsThanks>
                <FeedbackForm
                  onDiscard={handleDiscard}
                  onSubmit={handleSubmit}
                />
              </>
            ) : (
              <FeedbackSuccessMessage handleReset={handleReset} />
            )}
          </ModalContentWrapper>
        </DialogContentWrapper>
      </div>
    </DialogWrapper>
  );
};

export default FeedbackModal;
